import { useEffect, useRef, useState, type DragEvent } from "react";
import { Menu, Plus, Trash2 } from "lucide-react";
import { Panel } from "./panel";
import { Pager } from "./pager";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { hasTier } from "./access-control";
import {
  apiErrorMessage,
  useDecideRanges,
  useExposureRanges,
  useProposeRanges,
  useRangeLog,
  useReorderRanges,
  useSetRangeDisplay,
  ALL_SCOPE,
  type ExposureRangeLimits,
  type ExposureScope,
  type RangeDraftItem,
  type RangeProposal,
  type RangeType,
} from "../../pages/PortfolioPerformance/lib/api/exposure-tracking";

const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "—");

const STATUS_CLASS: Record<RangeProposal["status"], string> = {
  pending: "text-amber",
  approved: "text-approve",
  rejected: "text-decline",
};

/** "350000", "350,000", "$350k", "1.3m" -> dollars; null when it is not an amount. */
function parseAmount(text: string): number | null {
  const match = /^\$?\s*([\d,]*\.?\d+)\s*([km]?)$/i.exec(text.trim());
  if (!match) return null;
  const base = Number(match[1]!.replace(/,/g, ""));
  if (!Number.isFinite(base)) return null;
  const unit = match[2]!.toLowerCase();
  return unit === "k" ? base * 1_000 : unit === "m" ? base * 1_000_000 : base;
}

/** Dollars back into the short form the inputs accept: 350000 -> "350k", 1000000 -> "1m". */
function amountText(value: number | null): string {
  if (value == null) return "";
  if (value % 1_000_000 === 0) return `${value / 1_000_000}m`;
  if (value % 1_000 === 0) return `${value / 1_000}k`;
  return String(value);
}

/** The log shows the ranges by their limits only: where a range is shown is set directly on it, not proposed. */
const rangesText = (ranges: ExposureRangeLimits[]) => ranges.map((r) => r.label).join("  ·  ");

/** The most ranges there can be in total, every customer's and offices' together; the backend enforces it too (its `maxRanges`). */
const DEFAULT_MAX_RANGES = 5;

interface DraftRow {
  id: number;
  type: RangeType;
  /** "all" or an office's scope_key (franchise_finance ...) */
  scope: string;
  lower: string;
  upper: string;
}

/** What one draft row asks for, or why it can't be sent yet. */
function readRow(row: DraftRow, index: number): { item: RangeDraftItem } | { problem: string } {
  if (row.type === "any") {
    if (row.scope === ALL_SCOPE) return { problem: `Range ${index + 1}: an any-size range needs an office, such as Franchise Finance.` };
    return { item: { type: "any", scope: row.scope } };
  }
  const lower = parseAmount(row.lower);
  if (lower == null || lower <= 0) return { problem: `Range ${index + 1}: enter an amount above $0, like 350k or 1m.` };
  if (row.type === "above") return { item: { type: "above", lower, scope: row.scope } };
  const upper = parseAmount(row.upper);
  if (upper == null) return { problem: `Range ${index + 1}: enter the upper amount, like 750k.` };
  if (upper <= lower) return { problem: `Range ${index + 1}: the upper amount must be above the lower amount.` };
  return { item: { type: "between", lower, upper, scope: row.scope } };
}

const itemKey = (i: RangeDraftItem) => `${i.scope}|${i.type}|${i.lower ?? ""}|${i.upper ?? ""}`;

/** "All customers", or the office's name. */
const scopeName = (scope: string, scopes: ExposureScope[]) =>
  scope === ALL_SCOPE ? "All customers" : (scopes.find((s) => s.key === scope)?.label ?? scope);

function RangeRowEditor({
  index,
  row,
  scopes,
  disabled,
  onChange,
  onRemove,
}: {
  index: number;
  row: DraftRow;
  scopes: ExposureScope[];
  disabled: boolean;
  onChange: (patch: Partial<DraftRow>) => void;
  onRemove: () => void;
}) {
  const name = `Range ${index + 1}`;
  const hasScopes = scopes.length > 0;
  return (
    <div
      className={`grid gap-2 border border-border p-3 sm:items-center ${
        hasScopes ? "sm:grid-cols-[150px_150px_1fr_auto]" : "sm:grid-cols-[150px_1fr_auto]"
      }`}
    >
      {hasScopes ? (
        // Which customers the range looks at: everyone, or one office (Franchise Finance ...), the offices listed live from the database.
        // An office's range only counts that office's rows. "Any size" belongs to an office, so going back to All customers turns it
        // into a between range.
        <Select
          value={row.scope}
          onValueChange={(value) => onChange({ scope: value, ...(value === ALL_SCOPE && row.type === "any" ? { type: "between" as RangeType } : {}) })}
          disabled={disabled}
        >
          <SelectTrigger aria-label={`${name} scope`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SCOPE}>All customers</SelectItem>
            {scopes.map((s) => (
              <SelectItem key={s.key} value={s.key}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <Select value={row.type} onValueChange={(value) => onChange({ type: value as RangeType })} disabled={disabled}>
        <SelectTrigger aria-label={`${name} type`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="between">Between (e.g. 350k–750k)</SelectItem>
          <SelectItem value="above">Above (e.g. &gt;1m)</SelectItem>
          {row.scope !== ALL_SCOPE ? <SelectItem value="any">Any size</SelectItem> : null}
        </SelectContent>
      </Select>
      {row.type === "any" ? (
        <p className="text-xs text-muted-foreground">Every customer of {scopeName(row.scope, scopes)}, whatever the size.</p>
      ) : (
      // Four fixed slots (symbol, lower amount, "to", upper amount) so the inputs sit in the same place in every row: an Above range has
      // its ">" in the first slot and nothing in the last two; a Between range leaves the first slot empty (the same space the ">" takes)
      // and fills the last two. The lower input is the same width and starts at the same left edge either way.
      <div className="grid grid-cols-[1rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2">
        <span className="text-center text-muted-foreground" aria-hidden={row.type !== "above"}>
          {row.type === "above" ? ">" : ""}
        </span>
        <Input
          aria-label={`${name} ${row.type === "above" ? "amount" : "lower amount"}`}
          placeholder={row.type === "above" ? "Above, e.g. 1m" : "From, e.g. 350k"}
          inputMode="decimal"
          value={row.lower}
          onChange={(e) => onChange({ lower: e.target.value })}
          disabled={disabled}
        />
        {row.type === "between" ? (
          <>
            <span className="text-center text-muted-foreground">to</span>
            <Input
              aria-label={`${name} upper amount`}
              placeholder="To, e.g. 750k"
              inputMode="decimal"
              value={row.upper}
              onChange={(e) => onChange({ upper: e.target.value })}
              disabled={disabled}
            />
          </>
        ) : null}
      </div>
      )}
      <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onRemove} disabled={disabled} aria-label={`Remove ${name}`}>
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

/** The proposal form, seeded from the approved ranges. It is remounted (see `key` below) whenever the approved ranges change, so it
 *  never holds a stale draft after an approval. */
function RangeForm({
  current,
  scopes,
  maxRanges,
  pending,
}: {
  current: ExposureRangeLimits[];
  scopes: ExposureScope[];
  maxRanges: number;
  pending: boolean;
}) {
  const propose = useProposeRanges();
  const nextId = useRef(current.length);
  const seed = (): DraftRow[] =>
    current.map((r, i) => ({
      id: i,
      type: r.type,
      scope: r.scope,
      lower: r.type === "any" ? "" : amountText(r.lower),
      upper: amountText(r.upper),
    }));
  const [rows, setRows] = useState<DraftRow[]>(seed);
  const [reason, setReason] = useState("");

  const read = rows.map(readRow);
  const problem = read.find((r): r is { problem: string } => "problem" in r)?.problem ?? null;
  const items = read.flatMap((r) => ("item" in r ? [r.item] : []));
  const duplicate = !problem && new Set(items.map(itemKey)).size !== items.length;
  const draftKey = (r: ExposureRangeLimits) =>
    itemKey({ scope: r.scope, type: r.type, lower: r.type === "any" ? undefined : r.lower, upper: r.upper ?? undefined });
  const unchanged = !problem && items.map(itemKey).sort().join(",") === current.map(draftKey).sort().join(",");
  // At most `maxRanges` in total. At that number no range can be added: the Add range button and the reason box give way to a note until a
  // range is removed (or, with nothing changed yet, there is nothing to give a reason for). Editing a range's amounts still can be proposed.
  const atLimit = rows.length >= maxRanges;
  const error =
    problem ??
    (rows.length === 0
      ? "Add at least one range."
      : rows.length > maxRanges
        ? `At most ${maxRanges} ranges can be set. Remove ${rows.length - maxRanges} to propose.`
        : duplicate
          ? "The same range is listed twice."
          : null);
  const valid = !error && !unchanged && reason.trim() !== "";
  const showReason = !(atLimit && unchanged);

  function update(id: number, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  return (
    <form
      className="grid max-w-3xl gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || pending) return;
        propose.mutate({ ranges: items, reason: reason.trim() }, { onSuccess: () => setReason("") });
      }}
    >
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Proposed ranges</p>
      <p className="text-xs text-muted-foreground">
        Between: customers above the lower amount and up to the upper. Above: customers above the amount, with no upper limit.
        {scopes.length > 0
          ? " Scope picks whose customers the range looks at: everyone, or one office such as Franchise Finance (Any size is every customer of the office)."
          : ""}{" "}
        Up to {maxRanges} ranges in all. Where a range is shown (its card and its trend) is set on
        the approved range above, not proposed here.
      </p>
      {rows.map((row, index) => (
        <RangeRowEditor
          key={row.id}
          index={index}
          row={row}
          scopes={scopes}
          disabled={pending}
          onChange={(patch) => update(row.id, patch)}
          onRemove={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
        />
      ))}
      {atLimit ? (
        // No Add range button at the limit: a note says why, and that a range has to be removed to add a new one.
        <p role="status" className="text-xs text-amber">
          You can&apos;t add more than {maxRanges} ranges at once. Remove a range to add a new one.
        </p>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={pending}
            onClick={() =>
              setRows((prev) => [...prev, { id: nextId.current++, type: "between", scope: ALL_SCOPE, lower: "", upper: "" }])
            }
          >
            <Plus className="size-3.5" />
            Add range
          </Button>
        </div>
      )}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {showReason ? (
        <label className="grid gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Reason
          <Textarea
            placeholder="Why are the ranges changing?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
          />
        </label>
      ) : null}
      <div className="flex items-center gap-3">
        {showReason ? (
          <Button type="submit" size="sm" disabled={!valid || pending || propose.isPending}>
            {propose.isPending ? "Submitting…" : "Propose Change"}
          </Button>
        ) : null}
        {pending ? (
          <span className="text-xs text-amber">A proposal is pending approval — decide it in the log below first.</span>
        ) : null}
        {propose.isError ? <span className="text-xs text-destructive">{apiErrorMessage(propose.error)}</span> : null}
      </div>
    </form>
  );
}

/** One approved range with its Card / Trend switches. They save straight away -- no proposal, no approval -- and only a contributor
 *  (or admin) can use them; everyone else sees them as they are, disabled. */
interface ReorderHandlers {
  /** this tile is the one being dragged / the one a drop would land on */
  dragging: boolean;
  dropTarget: boolean;
  /** a move is being saved */
  busy: boolean;
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  /** the handle's arrow keys: -1 moves the range up, 1 down */
  onKeyMove: (delta: number) => void;
}

/** The most range cards that can be on at once when the server does not say (it does: `maxCards`). */
const DEFAULT_MAX_CARDS = 5;

/** One label and its on / off switch, side by side: a fixed-width label, then the switch right after it. */
function SwitchRow({
  label,
  checked,
  disabled,
  title,
  onChange,
  ariaLabel,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  title?: string;
  onChange: (on: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <label className="flex items-center gap-3 text-xs text-muted-foreground" title={title}>
      <span className="w-12">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={ariaLabel} />
    </label>
  );
}

/** One approved range, as a card: its name, and an on / off switch for its card on the page and one for its trend table and line. */
function ApprovedRange({
  range,
  canEdit,
  cardsFull,
  maxCards,
  reorder,
}: {
  range: ExposureRangeLimits;
  canEdit: boolean;
  /** as many range cards are on as the page allows, so a card that is off cannot be switched on until another is off */
  cardsFull: boolean;
  maxCards: number;
  /** Only given to a user who may reorder (a contributor or above): everyone else gets no handle at all, not a disabled one. */
  reorder?: ReorderHandlers;
}) {
  const setDisplay = useSetRangeDisplay();
  const busy = setDisplay.isPending;
  const save = (patch: { showCard?: boolean; showTrend?: boolean }) =>
    setDisplay.mutate({ rangeNo: range.rangeNo, showCard: range.showCard, showTrend: range.showTrend, ...patch });
  const name = range.label;
  return (
    <div
      data-range-tile
      onDragOver={reorder?.onDragOver}
      onDrop={reorder?.onDrop}
      className={`flex h-full min-w-0 flex-col gap-3 border bg-secondary p-3 shadow-sm ${reorder?.dropTarget ? "border-primary" : "border-border"} ${
        reorder?.dragging ? "opacity-50" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        {reorder ? (
          // The hamburger handle: drag the range up or down (the arrow keys do the same). The order is saved at once and is the order the
          // cards, trend lines and tables appear in on this page.
          <button
            type="button"
            draggable={!reorder.busy}
            disabled={reorder.busy}
            title="Drag to change the order"
            aria-label={`Move ${name}: drag it, or press the up or down arrow key`}
            onDragStart={reorder.onDragStart}
            onDragEnd={reorder.onDragEnd}
            onKeyDown={(event) => {
              if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                event.preventDefault();
                reorder.onKeyMove(-1);
              } else if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                event.preventDefault();
                reorder.onKeyMove(1);
              }
            }}
            className="flex h-6 w-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Menu className="size-4" aria-hidden="true" />
          </button>
        ) : null}
        <p className="min-w-0 break-words text-base font-semibold leading-snug tabular-nums text-foreground">{range.label}</p>
      </div>
      <div className="space-y-2">
        <SwitchRow
          label="Card"
          checked={range.showCard}
          disabled={!canEdit || busy || (!range.showCard && cardsFull)}
          title={!range.showCard && cardsFull ? `At most ${maxCards} cards can be on at once. Turn another one off first.` : undefined}
          onChange={(on) => save({ showCard: on })}
          ariaLabel={`${name} card`}
        />
        <SwitchRow
          label="Trend"
          checked={range.showTrend}
          disabled={!canEdit || busy}
          onChange={(on) => save({ showTrend: on })}
          ariaLabel={`${name} trend`}
        />
      </div>
      {setDisplay.isError ? <p className="text-xs text-destructive">{apiErrorMessage(setDisplay.error)}</p> : null}
    </div>
  );
}

function RangeSetup() {
  const rangesQ = useExposureRanges();
  const canPropose = hasTier("reviewer");
  const canSetDisplay = hasTier("contributor");
  const available = rangesQ.data?.available ?? false;
  const pending = rangesQ.data?.hasPending ?? false;
  const ranges = rangesQ.data?.ranges ?? [];
  // Neither the show flags nor the order: switching a card or trend, or moving a range, must not throw away a proposal being drafted below.
  const approvedKey = ranges.map((r) => `${r.scope}|${r.type}|${r.lower}|${r.upper ?? ""}`).sort().join(",");

  // Reordering (contributors and above): the order just chosen is shown at once and saved straight away, then the page's own data takes
  // over. A move renumbers the ranges, so the numbers held here are dropped as soon as new data arrives.
  const reorder = useReorderRanges();
  const [dragNo, setDragNo] = useState<number | null>(null);
  const [overNo, setOverNo] = useState<number | null>(null);
  const [localOrder, setLocalOrder] = useState<number[] | null>(null);
  useEffect(() => setLocalOrder(null), [rangesQ.dataUpdatedAt]);
  const shown = localOrder
    ? [...ranges].sort((a, b) => localOrder.indexOf(a.rangeNo) - localOrder.indexOf(b.rangeNo))
    : ranges;
  const canReorder = canSetDisplay && available && ranges.length > 1;
  // Only so many range cards can be on at once: once that many are, the switch of a card that is off is disabled.
  const maxCards = rangesQ.data?.maxCards ?? DEFAULT_MAX_CARDS;
  const cardsOn = ranges.filter((r) => r.showCard).length;

  function moveRange(rangeNo: number, toIndex: number) {
    const order = shown.map((r) => r.rangeNo);
    const from = order.indexOf(rangeNo);
    if (from < 0) return;
    order.splice(from, 1);
    order.splice(Math.max(0, Math.min(toIndex, order.length)), 0, rangeNo);
    if (order.join(",") === shown.map((r) => r.rangeNo).join(",")) return;
    setLocalOrder(order);
    reorder.mutate(order, { onError: () => setLocalOrder(null) });
  }

  return (
    <Panel
      className="mt-6"
      title="Customer Exposure Review Threshold"
      description="The customer exposure ranges behind the cards, the Customer Exposure Trend chart and the tables above (a customer is in a range when its exposure is above the lower amount and up to the upper, or above the amount for an open-ended range). Changing the ranges themselves only takes effect once a contributor approves it; the Card and Trend choices are set directly."
    >
      {rangesQ.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <div className="space-y-5">
          {/* Every card the same size, and from lg up ALL of them in one row (one equal column each, however many ranges there are: at most
              five), so the cards are compact and their names wrap inside them. Smaller screens fall back to two or three to a row. All
              cards in a row are as tall as the tallest. */}
          <div className="grid auto-rows-fr grid-cols-2 gap-3 md:grid-cols-3 lg:auto-cols-fr lg:grid-flow-col lg:grid-cols-none">
            {shown.map((r, index) => (
              <ApprovedRange
                key={r.rangeNo}
                range={r}
                canEdit={canSetDisplay && available}
                cardsFull={cardsOn >= maxCards}
                maxCards={maxCards}
                reorder={
                  canReorder
                    ? {
                        dragging: dragNo === r.rangeNo,
                        dropTarget: dragNo !== null && dragNo !== r.rangeNo && overNo === r.rangeNo,
                        busy: reorder.isPending,
                        onDragStart: (event) => {
                          setDragNo(r.rangeNo);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", String(r.rangeNo));
                          // Drag the whole tile's picture, not just the handle's.
                          const tile = event.currentTarget.closest("[data-range-tile]");
                          if (tile) event.dataTransfer.setDragImage(tile, 16, 16);
                        },
                        onDragEnd: () => {
                          setDragNo(null);
                          setOverNo(null);
                        },
                        onDragOver: (event) => {
                          if (dragNo === null) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          setOverNo(r.rangeNo);
                        },
                        onDrop: (event) => {
                          event.preventDefault();
                          if (dragNo !== null) moveRange(dragNo, index);
                          setDragNo(null);
                          setOverNo(null);
                        },
                        onKeyMove: (delta) => moveRange(r.rangeNo, index + delta),
                      }
                    : undefined
                }
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Card shows the range at the top of the page (with Download CSV); Trend shows its table and line under the Customer Exposure Trend
            chart. Up to {maxCards} cards can be on at once ({cardsOn} on now).{" "}
            {canSetDisplay ? "Changing them takes effect at once." : "Only contributors and above can change them."}
            {canReorder
              ? " Drag a range by its ≡ handle to change the order the cards, trend lines and tables appear in; that takes effect at once too."
              : ""}
          </p>
          {rangesQ.data?.updatedBy ? (
            <p className="text-xs text-muted-foreground">
              Last changed by {rangesQ.data.updatedBy}
              {rangesQ.data.updatedAt ? ` · ${dateTime(rangesQ.data.updatedAt)}` : ""}
              {rangesQ.data.note ? ` · ${rangesQ.data.note}` : ""}
            </p>
          ) : null}

          {!available ? (
            <p className="text-xs text-amber">
              Exposure ranges are not set up in this environment yet, so the defaults are showing and a change cannot be proposed.
            </p>
          ) : canPropose ? (
            <RangeForm
              key={approvedKey}
              current={ranges}
              scopes={rangesQ.data?.scopes ?? []}
              maxRanges={rangesQ.data?.maxRanges ?? DEFAULT_MAX_RANGES}
              pending={pending}
            />
          ) : (
            <p className="text-xs text-muted-foreground">Only reviewers and above can propose a change to the ranges.</p>
          )}
        </div>
      )}
    </Panel>
  );
}

function DecisionCell({ proposal }: { proposal: RangeProposal }) {
  const decide = useDecideRanges();
  const [notes, setNotes] = useState("");
  return (
    <div className="grid min-w-[200px] gap-2">
      <Input
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={decide.isPending}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "approved", notes })}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "rejected", notes })}
        >
          Reject
        </Button>
      </div>
      {decide.isError ? <span className="text-xs text-destructive">{apiErrorMessage(decide.error)}</span> : null}
    </div>
  );
}

function RangeLog() {
  const [offset, setOffset] = useState(0);
  const logQ = useRangeLog(offset);
  const canApprove = hasTier("contributor");

  return (
    <Panel
      className="mt-6"
      title="Customer Exposure Review Threshold Proposal Log"
      description="Every proposed change to the ranges, newest first, with who proposed and who approved or rejected it."
    >
      {logQ.isError ? (
        <p className="text-sm text-destructive">Could not load the proposal log.</p>
      ) : !logQ.data ? (
        <Skeleton className="h-32 w-full" />
      ) : logQ.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No proposals yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2">Proposed</th>
                  <th className="pb-2">Change</th>
                  <th className="pb-2">Reason</th>
                  <th className="pb-2">Proposed By</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Reviewed</th>
                  <th className="pb-2">Notes</th>
                  {canApprove ? <th className="pb-2">Decision</th> : null}
                </tr>
              </thead>
              <tbody>
                {logQ.data.items.map((p) => (
                  <tr key={p.id} className="border-t border-border align-top">
                    <td className="py-2 pr-3 text-muted-foreground">{dateTime(p.proposedAt)}</td>
                    <td className="py-2 pr-3 tabular-nums">
                      <div className="text-muted-foreground">{rangesText(p.currentRanges)}</div>
                      <div>→ {rangesText(p.proposedRanges)}</div>
                    </td>
                    <td className="py-2 pr-3">{p.reason}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.proposedBy ?? "—"}</td>
                    <td className={`py-2 pr-3 font-medium capitalize ${STATUS_CLASS[p.status]}`}>{p.status}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {p.reviewedBy ? `${p.reviewedBy} · ${dateTime(p.reviewedAt)}` : "—"}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.reviewNotes ?? ""}</td>
                    {canApprove ? (
                      <td className="py-2">{p.status === "pending" ? <DecisionCell proposal={p} /> : null}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} total={logQ.data.total} isFetching={logQ.isFetching} onChange={setOffset} />
        </>
      )}
    </Panel>
  );
}

export function ExposureRangesSection() {
  return (
    <>
      <RangeSetup />
      <RangeLog />
    </>
  );
}
